import type { TweakPrefs } from './types';

const STORAGE_KEY = 'bnp.calendar.tweaks.v1';

const DEFAULTS: TweakPrefs = {
    density: 'comfy',
    groupByAccom: true,
    showWeekends: true,
    priceDisplay: 'number',
    showGuestNames: true,
};

export function loadPrefs(): TweakPrefs {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
    } catch { /* ignore */ }
    return { ...DEFAULTS };
}

function savePrefs(prefs: TweakPrefs): void {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)); } catch { /* ignore */ }
}

export function renderTweaksPanel(
    root: HTMLElement,
    prefs: TweakPrefs,
    i18n: Record<string, string>,
    onChange: (prefs: TweakPrefs) => void
): void {
    const existing = root.querySelector('.tweaks-panel');
    existing?.remove();

    const panel = document.createElement('div');
    panel.className = 'tweaks-panel availability-calendar';
    panel.innerHTML = `
      <button class="tweaks-toggle" title="${i18n['tweaks'] || 'Tweaks'}" aria-label="Tweaks">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"/>
        </svg>
      </button>
      <div class="tweaks-drawer" role="dialog" aria-label="${i18n['tweaks'] || 'Tweaks'}">
        <div class="tweaks-section-title">${i18n['layout'] || 'Layout'}</div>

        <div class="tweaks-row">
          <span>${i18n['density'] || 'Density'}</span>
          <div class="tweaks-seg js-density">
            ${seg('compact', i18n['compact'] || 'Compact', prefs.density === 'compact')}
            ${seg('comfy',   i18n['comfy']   || 'Comfy',   prefs.density === 'comfy')}
            ${seg('spacious',i18n['spacious']|| 'Spacious', prefs.density === 'spacious')}
          </div>
        </div>

        <div class="tweaks-row">
          <span>${i18n['groupByAccom'] || 'Group by accommodation'}</span>
          <input type="checkbox" class="form-check-input js-group-by-accom" ${prefs.groupByAccom ? 'checked' : ''}>
        </div>

        <div class="tweaks-row">
          <span>${i18n['highlightWeekends'] || 'Highlight weekends'}</span>
          <input type="checkbox" class="form-check-input js-show-weekends" ${prefs.showWeekends ? 'checked' : ''}>
        </div>

        <div class="tweaks-section-title">${i18n['priceDisplay'] || 'Price display'}</div>

        <div class="tweaks-row">
          <span>${i18n['priceDisplay'] || 'Style'}</span>
          <div class="tweaks-seg js-price-display">
            ${seg('number', i18n['number'] || 'Number', prefs.priceDisplay === 'number')}
            ${seg('heat',   i18n['heatmap'] || 'Heatmap', prefs.priceDisplay === 'heat')}
            ${seg('both',   i18n['both']    || 'Both',    prefs.priceDisplay === 'both')}
            ${seg('hover',  i18n['hoverOnly']|| 'Hover',  prefs.priceDisplay === 'hover')}
          </div>
        </div>

        <div class="tweaks-section-title">${i18n['bookings'] || 'Bookings'}</div>

        <div class="tweaks-row">
          <span>${i18n['showGuestNames'] || 'Show guest names'}</span>
          <input type="checkbox" class="form-check-input js-show-guest-names" ${prefs.showGuestNames ? 'checked' : ''}>
        </div>
      </div>`;

    root.appendChild(panel);

    // Toggle drawer
    const toggle = panel.querySelector<HTMLButtonElement>('.tweaks-toggle')!;
    const drawer = panel.querySelector<HTMLElement>('.tweaks-drawer')!;
    toggle.addEventListener('click', () => drawer.classList.toggle('open'));
    document.addEventListener('click', e => {
        if (!panel.contains(e.target as Node)) drawer.classList.remove('open');
    });

    // Density
    panel.querySelectorAll<HTMLButtonElement>('.js-density .tweaks-seg-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            panel.querySelectorAll('.js-density .tweaks-seg-btn').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');
            const p = { ...loadPrefs(), density: btn.dataset.val as TweakPrefs['density'] };
            savePrefs(p); onChange(p);
        });
    });

    // Group by accom
    panel.querySelector<HTMLInputElement>('.js-group-by-accom')?.addEventListener('change', e => {
        const p = { ...loadPrefs(), groupByAccom: (e.target as HTMLInputElement).checked };
        savePrefs(p); onChange(p);
    });

    // Show weekends
    panel.querySelector<HTMLInputElement>('.js-show-weekends')?.addEventListener('change', e => {
        const p = { ...loadPrefs(), showWeekends: (e.target as HTMLInputElement).checked };
        savePrefs(p); onChange(p);
    });

    // Price display
    panel.querySelectorAll<HTMLButtonElement>('.js-price-display .tweaks-seg-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            panel.querySelectorAll('.js-price-display .tweaks-seg-btn').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');
            const p = { ...loadPrefs(), priceDisplay: btn.dataset.val as TweakPrefs['priceDisplay'] };
            savePrefs(p); onChange(p);
        });
    });

    // Show guest names
    panel.querySelector<HTMLInputElement>('.js-show-guest-names')?.addEventListener('change', e => {
        const p = { ...loadPrefs(), showGuestNames: (e.target as HTMLInputElement).checked };
        savePrefs(p); onChange(p);
    });
}

function seg(val: string, label: string, active: boolean): string {
    return `<button class="tweaks-seg-btn${active ? ' active' : ''}" data-val="${val}">${label}</button>`;
}
